import { MutationCache, QueryClient, QueryClientProvider, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { ClientEkera } from './api/client';
import type { NatureDepot } from './api/types';
import type { FournisseurIdentite } from './auth/identite';
import { Erreur, Chargement } from './composants/communs';
import { Cadre } from './composants/Cadre';
import { ErrorBoundary } from './composants/ErrorBoundary';
import { ContexteEkera, type Contexte } from './contexte';
import { effacerComplements } from './domaine/complementsLocaux';
import { NATURES_DEPOT } from './domaine/depots';
import { oublierHistorique } from './domaine/console';
import { EcranCarte } from './ecrans/Carte';
import { EcranApplication } from './ecrans/Application';
import { EcranAide } from './ecrans/Aide';
import { EcranCollecteurs } from './ecrans/Collecteurs';
import { EcranConstats } from './ecrans/Constats';
import { EcranAbonnement } from './ecrans/Abonnement';
import { EcranCles } from './ecrans/Cles';
import { EspaceCircuit, PageCircuits } from './ecrans/CircuitsPage';
import { ErreurApi } from './api/client';
import { EcranCorrections } from './ecrans/Corrections';
import { EcranDecisions } from './ecrans/Decisions';
import { EcranDepot, PremiereCarte } from './ecrans/Depot';
import { EcranEntretien } from './ecrans/Entretien';
import { EcranDocuments } from './ecrans/Documents';
import { EcranExports } from './ecrans/Exports';
import { EcranInterroger } from './ecrans/Interroger';
import { EcranRetours } from './ecrans/Retours';
import { EcranLicences } from './ecrans/Licences';
import { EcranSources } from './ecrans/Sources';
import { EcranTableau } from './ecrans/Tableau';
import { titrePage, useRoute } from './routeur';

/** Client de requêtes de l'application. */
export function creerQueryClient(): QueryClient {
  const q: QueryClient = new QueryClient({
    defaultOptions: { queries: { retry: 1, staleTime: 30_000, refetchOnWindowFocus: false } },
    // W2 : une écriture refusée (403) peut venir de droits changés depuis le dernier jeton : on relit /v0/moi.
    // L'API reste l'autorité ; l'écran ne fait que se remettre à jour.
    mutationCache: new MutationCache({
      onError: (e) => {
        if (e instanceof ErreurApi && e.statut === 403) void q.invalidateQueries({ queryKey: ['moi'] });
      },
    }),
  });
  return q;
}

export function App({
  identite,
  client: clientFourni,
  carteSouhaitee,
  demonstration = false,
}: {
  identite: FournisseurIdentite;
  client?: ClientEkera;
  /** Carte affichée au démarrage (/config.json), sinon VITE_EKERA_CARTE, sinon la première. */
  carteSouhaitee?: string;
  /** Démonstration déclarée par /config.json (`demonstration: true`) ; faux par défaut. */
  demonstration?: boolean;
}) {
  const qc = useMemo(() => creerQueryClient(), []);
  const client = useMemo(() => clientFourni ?? new ClientEkera(identite), [clientFourni, identite]);
  return (
    <ErrorBoundary><QueryClientProvider client={qc}>
      <Demarrage identite={identite} client={client} carteSouhaitee={carteSouhaitee} demonstration={demonstration} />
    </QueryClientProvider></ErrorBoundary>
  );
}

/** Nature de pièce proposée par un lien (« Déposer l’export » depuis « Ajouter une source »). */
function natureDepuis(params: URLSearchParams): NatureDepot | null {
  const n = params.get('nature');
  return n && (NATURES_DEPOT as readonly string[]).includes(n) ? (n as NatureDepot) : null;
}

/** Pourquoi l'écriture n'est pas proposée : l'API (lecture seule) ou le rôle de la personne (jeton). */
export function motifLectureSeule(apiEcrit: boolean, roles: readonly string[]): string {
  if (!apiEcrit) return 'L’API est en lecture seule (aucun droit d’écriture configuré).';
  if (!roles.includes('ecriture'))
    return roles.includes('partage')
      ? 'Votre rôle est le partage hors de l’entreprise, sans correction de la carte (rôle « écriture ») : les formulaires d’écriture ne vous sont pas proposés.'
      : 'Votre rôle sur Ekera est la lecture seule : la carte, les constats et les exports vous sont ouverts.';
  return '';
}

function Demarrage({
  identite,
  client,
  carteSouhaitee,
  demonstration,
}: {
  identite: FournisseurIdentite;
  client: ClientEkera;
  carteSouhaitee?: string;
  demonstration: boolean;
}) {
  const route = useRoute();
  const qc = useQueryClient();
  // Session expirée ou close : l'écran de connexion revient.
  const [, rafraichir] = useReducer((n: number) => n + 1, 0);
  useEffect(() => identite.surChangement?.(rafraichir), [identite]);
  // W2 : après un renouvellement du jeton, les rôles et l'organisation sont relus (GET /v0/moi) au lieu de garder ceux
  // d'avant. Aucun droit n'est décidé ici : l'API applique les siens à chaque appel.
  useEffect(
    () =>
      identite.surRenouvellement?.(() => {
        void qc.invalidateQueries({ queryKey: ['moi'] });
        void qc.invalidateQueries({ queryKey: ['circuits', 'moi'] });
      }),
    [identite, qc],
  );
  const connecte = identite.utilisateur() !== null;
  // O42 : les compléments de décision gardés en mémoire ne survivent ni à la fin de la session, ni à un changement de
  // personne.
  const sujet = identite.utilisateur()?.sujet ?? null;
  const sujetPrecedent = useRef(sujet);
  useEffect(() => {
    if (!connecte || sujet !== sujetPrecedent.current) {
      effacerComplements();
      oublierHistorique(); // les questions de la session ne passent pas à la personne suivante
    }
    sujetPrecedent.current = sujet;
  }, [connecte, sujet]);
  const sante = useQuery({ queryKey: ['sante'], queryFn: () => client.sante() });
  const moi = useQuery({ queryKey: ['moi', identite.utilisateur()?.sujet], queryFn: () => client.moi(), enabled: connecte, retry: false });
  const cartes = useQuery({ queryKey: ['cartes'], queryFn: () => client.cartes(), enabled: connecte && !!moi.data && route.page !== 'abonnement' });
  // Une personne sans rôle d'accès (directeur financier, achats, direction) peut tenir un rôle de circuit : elle n'a
  // que l'espace du circuit, sans aucun écran de la carte (CV-S11).
  const sansRole = moi.error instanceof ErreurApi && moi.error.statut === 403;
  const circuitSeul = useQuery({ queryKey: ['circuits', 'moi'], queryFn: () => client.circuitsMoi(), enabled: connecte && sansRole, retry: false });
  const souhaitee = carteSouhaitee ?? (import.meta.env.VITE_EKERA_CARTE as string | undefined) ?? null;
  const [choisie, setChoisie] = useState<string | null>(null);

  if (!connecte) {
    return (
      <main className="pile" style={{ padding: 32, maxWidth: 520 }}>
        <h1>Ekera</h1>
        <p>Connectez-vous avec le compte de votre entreprise.</p>
        <button type="button" className="btn principal" onClick={() => void identite.connecter()}>
          Se connecter
        </button>
      </main>
    );
  }
  if (route.page === 'abonnement' && moi.data) return <main className="pile" style={{ padding: 32 }}><h1>Abonnement et factures</h1><EcranAbonnement identite={identite} administrateur={moi.data.roles.includes('administration')} /><a href="#/tableau">Retour au tableau de bord</a></main>;
  if (sansRole && circuitSeul.data) return <EspaceCircuit client={client} identite={identite} route={route} demonstration={demonstration} />;
  if (sansRole && circuitSeul.isPending) return <main style={{ padding: 32 }}><Chargement quoi="du circuit de validation" /></main>;
  if (sante.error || moi.error || cartes.error)
    return (
      <main className="pile" style={{ padding: 32 }}>
        <Erreur erreur={sante.error ?? moi.error ?? cartes.error} titre="L’API d’Ekera refuse ou ne répond pas." />
        {identite.mode === 'oidc' && (
          <p>
            <button
              type="button"
              className="btn"
              onClick={() => {
                effacerComplements();
                oublierHistorique();
                void identite.deconnecter();
              }}
            >
              Se déconnecter
            </button>
          </p>
        )}
      </main>
    );
  if (!sante.data || !moi.data || !cartes.data)
    return (
      <main style={{ padding: 32 }}>
        <Chargement quoi="de l’API" />
      </main>
    );
  const liste = cartes.data;
  const cid = choisie ?? (liste.find((c) => c.id === souhaitee)?.id ?? liste[0]?.id ?? null);
  if (!cid && route.page !== 'retours')
    return (
      <main className="pile" style={{ padding: 32, maxWidth: 640 }}>
        <p role="alert">Aucune carte n’est chargée dans l’API.</p>
        {moi.data.roles.includes('administration') ? (
          <PremiereCarte client={client} surCreee={(id) => setChoisie(id)} />
        ) : (
          <p>Une personne ayant le rôle « administration » peut commencer le diagnostic de l’entreprise.</p>
        )}
      </main>
    );
  const motif = motifLectureSeule(sante.data.ecriture_autorisee, moi.data.roles);
  const ctx: Contexte = {
    client,
    identite,
    cid: cid ?? '',
    moi: moi.data,
    sante: sante.data,
    ecritureAutorisee: motif === '',
    administrateur: moi.data.roles.includes('administration'),
    peutPartager: moi.data.roles.includes('partage'),
    motifLectureSeule: motif,
    demonstration,
  };
  const entreprise = liste.find((c) => c.id === cid)?.entreprise ?? cid ?? 'Votre entreprise';
  return (
    <ContexteEkera.Provider value={ctx}>
      <Cadre page={route.page} titre={titrePage(route)} entreprise={entreprise} cartes={liste} surChangerCarte={setChoisie}>
        {route.page === 'tableau' && <EcranTableau />}
        {route.page === 'depot' && <EcranDepot natureInitiale={natureDepuis(route.params)} />}
        {route.page === 'entretien' && <EcranEntretien delegation={route.params.get('delegation')} />}
        {route.page === 'carte' && <EcranCarte params={route.params} />}
        {route.page === 'application' && <EcranApplication key={cid} params={route.params} />}
        {route.page === 'constats' && <EcranConstats params={route.params} />}
        {route.page === 'corrections' && <EcranCorrections params={route.params} />}
        {route.page === 'decisions' && <EcranDecisions params={route.params} />}
        {route.page === 'documents' && <EcranDocuments params={route.params} />}
        {route.page === 'licences' && <EcranLicences />}
        {route.page === 'sources' && <EcranSources params={route.params} />}
        {route.page === 'collecteurs' && <EcranCollecteurs params={route.params} />}
        {route.page === 'exports' && <EcranExports />}
        {route.page === 'circuits' && <PageCircuits params={route.params} />}
        {route.page === 'cles' && <EcranCles />}
        {route.page === 'interroger' && <EcranInterroger />}
        {route.page === 'retours' && <EcranRetours params={route.params} />}
        {route.page === 'aide' && <EcranAide />}
      </Cadre>
    </ContexteEkera.Provider>
  );
}
